import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';
import './Pages.css';

type Mode = 'password' | 'signup' | 'magic' | 'reset';

const inputStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 14,
  border: '1px solid var(--border-color, #d0d0d0)',
  borderRadius: 6,
  background: 'var(--bg-primary, #fff)',
  color: 'var(--text-primary, #111)',
};

const linkBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'var(--accent-color, #2563eb)',
  cursor: 'pointer',
  fontSize: 13,
  textDecoration: 'underline',
};

export const LoginPage: React.FC = () => {
  const {
    signInWithGoogle,
    sendEmailLink,
    emailLinkSent,
    resetEmailLinkSent,
    signInWithPassword,
    signUpWithPassword,
    sendPasswordReset,
    passwordResetSent,
    resetPasswordResetSent,
    clearError,
    error,
    loading,
  } = useAuth();
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Predictive wake-up: warm the backend while the user is reading/typing
  useEffect(() => {
    fetch(`${config.API_BASE_URL}/health`, { method: 'GET' }).catch(() => {});
  }, []);

  const switchMode = (next: Mode) => {
    clearError();
    if (emailLinkSent) resetEmailLinkSent();
    if (passwordResetSent) resetPasswordResetSent();
    setPassword('');
    setMode(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      if (mode === 'password') {
        await signInWithPassword(email, password);
      } else if (mode === 'signup') {
        await signUpWithPassword(email, password);
      } else if (mode === 'magic') {
        await sendEmailLink(email);
      } else if (mode === 'reset') {
        await sendPasswordReset(email);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderConfirmation = (icon: string, heading: string, body: React.ReactNode, onBackLabel: string) => (
    <div style={{ textAlign: 'center', padding: '12px 4px' }}>
      <div style={{ fontSize: 36, marginBottom: 8 }}>{icon}</div>
      <h3 style={{ margin: '0 0 8px' }}>{heading}</h3>
      <p style={{ margin: 0, color: 'var(--text-secondary, #555)' }}>{body}</p>
      <button
        type="button"
        className="google-signin-btn"
        style={{ marginTop: 16 }}
        onClick={() => switchMode('password')}
      >
        {onBackLabel}
      </button>
    </div>
  );

  const headingFor: Record<Mode, string> = {
    password: 'Welcome',
    signup: 'Create your account',
    magic: 'Sign in with a magic link',
    reset: 'Reset your password',
  };

  const subFor: Record<Mode, string> = {
    password: 'Sign in to manage your resumes and job descriptions.',
    signup: 'New here? Pick a password to create your account.',
    magic: "We'll email you a one-time link — no password needed.",
    reset: "Enter your email and we'll send you a reset link.",
  };

  const submitLabelFor: Record<Mode, string> = {
    password: submitting ? 'Signing in...' : 'Sign in',
    signup: submitting ? 'Creating account...' : 'Create account',
    magic: submitting ? 'Sending link...' : 'Email me a sign-in link',
    reset: submitting ? 'Sending...' : 'Send reset link',
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-logo">
          <div className="login-brand-text">
            <span className="login-brand-name">xRank</span>
            <span className="login-brand-tagline">Explainable AI Ranking</span>
          </div>
          <p className="login-subtitle">AI-powered resume ranking & analysis</p>
        </div>

        <div className="login-card">
          <h2>{headingFor[mode]}</h2>
          <p>{subFor[mode]}</p>

          {error && <div className="login-error">{error}</div>}

          {emailLinkSent && mode === 'magic' ? (
            renderConfirmation(
              '📬',
              'Check your inbox',
              <>
                We sent a sign-in link to <strong>{email}</strong>. Click the link in
                that email to finish signing in.
              </>,
              'Use a different email'
            )
          ) : passwordResetSent && mode === 'reset' ? (
            renderConfirmation(
              '🔐',
              'Reset link sent',
              <>
                If an account exists for <strong>{email}</strong>, you'll receive a
                password reset email shortly.
              </>,
              'Back to sign in'
            )
          ) : (
            <>
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting || loading}
                  style={inputStyle}
                />
                {(mode === 'password' || mode === 'signup') && (
                  <input
                    type="password"
                    required
                    minLength={6}
                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                    placeholder={mode === 'signup' ? 'At least 6 characters' : 'Password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={submitting || loading}
                    style={inputStyle}
                  />
                )}
                <button
                  type="submit"
                  className="google-signin-btn"
                  disabled={
                    submitting ||
                    loading ||
                    !email.trim() ||
                    ((mode === 'password' || mode === 'signup') && password.length < 6)
                  }
                >
                  {submitLabelFor[mode]}
                </button>
              </form>

              <div style={{ marginTop: 10, fontSize: 13, display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
                {mode === 'password' && (
                  <>
                    <button type="button" style={linkBtnStyle} onClick={() => switchMode('reset')}>
                      Forgot password?
                    </button>
                    <button type="button" style={linkBtnStyle} onClick={() => switchMode('signup')}>
                      Create account
                    </button>
                    <button type="button" style={linkBtnStyle} onClick={() => switchMode('magic')}>
                      Email me a link instead
                    </button>
                  </>
                )}
                {mode === 'signup' && (
                  <button type="button" style={linkBtnStyle} onClick={() => switchMode('password')}>
                    Already have an account? Sign in
                  </button>
                )}
                {mode === 'magic' && (
                  <button type="button" style={linkBtnStyle} onClick={() => switchMode('password')}>
                    Use password instead
                  </button>
                )}
                {mode === 'reset' && (
                  <button type="button" style={linkBtnStyle} onClick={() => switchMode('password')}>
                    Back to sign in
                  </button>
                )}
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  margin: '14px 0',
                  color: 'var(--text-secondary, #888)',
                  fontSize: 12,
                }}
              >
                <span style={{ flex: 1, height: 1, background: 'var(--border-color, #e0e0e0)' }} />
                <span>OR</span>
                <span style={{ flex: 1, height: 1, background: 'var(--border-color, #e0e0e0)' }} />
              </div>

              <button
                className="google-signin-btn"
                onClick={signInWithGoogle}
                disabled={loading}
              >
                <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {loading ? 'Signing in...' : 'Continue with Google'}
              </button>
            </>
          )}
        </div>

        <p className="login-footer">
          Powered by Aram AI Labs
        </p>
      </div>
    </div>
  );
};
